<template>
  <v-card class="fill-height">
    <v-card-title class="heading mb-4">
      <Tooltip
        tooltipTitle="Vehicle ID"
        v-if="vehicleDetails.equipment_id"
        :tooltipText="vehicleDetails.equipment_id"
        right
        tooltipClass="text-h5 font-weight-medium"
      />
    </v-card-title>

    <v-card-subtitle class="subHeading py-2">
      <div class="d-flex justify-space-between">
        <Tooltip
          tooltipTitle="Vehicle Type"
          v-if="vehicleDetails.vehicle_type_title"
          :tooltipText="vehicleDetails.vehicle_type_title"
          right
          tooltipClass="text-subtitle font-weight-medium"
        />

        <Tooltip
          tooltipTitle="Vehicle Number"
          v-if="vehicleDetails.vehicle_number"
          :tooltipText="vehicleDetails.vehicle_number"
          left
          tooltipClass="text-subtitle font-weight-medium"
        />
      </div>
    </v-card-subtitle>

    <v-card-text class="pt-2" v-if="Object.keys(vehicleDetails).length">
      <v-tabs grow v-model="detailsTab" class="mb-2">
        <v-tabs-slider color="white"></v-tabs-slider>
        <v-tab name="vehicle-details-view">Detail View</v-tab>
        <v-tab name="asset-checklist-view">Vehicle CheckList</v-tab>
        <v-tab name="vehicle-details-maintenance">Vehicle Maintenance</v-tab>
        <v-tab name="vehicle-details-documents">Documents</v-tab>
        <v-tab name="vehicle-details-logs">Logs</v-tab>
      </v-tabs>

      <v-tabs-items v-model="detailsTab">
        <v-tab-item class="Height-Class">
          <v-expansion-panels focusable multiple v-model="vehicleDetailView">
            <CardDetails
              title="Vehicle Info"
              :displayObject="vehicleDetails.extra_info"
            />
            <CardDetails
              title="Company Info"
              :displayObject="vehicleDetails.company_detail"
            />
          </v-expansion-panels>
        </v-tab-item>

        <v-tab-item class="Height-Class">
          <div v-if="assetChecklistData && assetChecklistData.length">
            <v-data-table
              :headers="assetChecklistHeaders"
              :items="assetChecklistData"
              class="elevation-1"
              hide-default-footer
            >
              <template v-slot:[`item.actions`]="{ item, index }">
                <v-icon
                  small
                  class="mr-2"
                  @click="viewAssetChecklist(item, index)"
                  color="primary"
                >
                  mdi-eye
                </v-icon>
              </template>

              <template v-slot:item.is_submitted="{ item }">
                <v-icon v-if="item.is_submitted" color="success" small>
                  mdi-check-circle
                </v-icon>
                <v-icon v-else color="error" small> mdi-close-circle </v-icon>
              </template>

              <template v-slot:item.is_passed="{ item }">
                <v-icon v-if="item.is_passed" color="success" small>
                  mdi-check-circle
                </v-icon>
                <v-icon v-else color="error" small> mdi-close-circle </v-icon>
              </template>
            </v-data-table>
          </div>

          <div v-else class="text-center">
            <v-divider class="mb-4"></v-divider>
            <span>No Asset Checklist data to show</span>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="text-right">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-if="permissions && permissions.add"
                  color="primary"
                  name="vehicle-details-add-maintenance"
                  depressed
                  v-on="on"
                  v-bind="attrs"
                  small
                  class="mb-2"
                  @click="addMaintenance"
                >
                  <v-icon>mdi-tools</v-icon>
                </v-btn>
              </template>
              <span>Add Maintenance</span>
            </v-tooltip>
          </div>

          <div
            class="Maintenance-Height-Class"
            v-if="vehicleMaintenance && vehicleMaintenance.length"
          >
            <v-card
              color="subHeading"
              v-for="(item, i) in vehicleMaintenance"
              :key="i"
            >
              <v-card-title class="pb-1 pt-3">
                <span class="text-subtitle-1 font-weight-medium">
                  {{ `${i + 1}. ${item.maintenance_type}` }}
                </span>

                <v-spacer></v-spacer>

                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="item.maintenance_status !== 'Expired'"
                      :name="`vehicle-details-edit-maintenance-${i}`"
                      color="primary"
                      @click="editMaintenance(item.id)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <!-- Complete Maintenance Button -->
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="item.maintenance_status !== 'Expired'"
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      @click="updateMAintenanceComplete(item.id)"
                    >
                      mdi-check
                    </v-icon>
                  </template>
                  <span>Complete Maintenance</span>
                </v-tooltip>
              </v-card-title>

              <v-divider></v-divider>
              <v-card-text class="pt-2 pb-3">
                <table class="Normal-Table">
                  <tbody>
                    <tr>
                      <th>Priority</th>
                      <th class="px-1">:</th>
                      <td>{{ item.priority }}</td>
                    </tr>
                    <tr>
                      <th>Duration</th>
                      <th class="px-1">:</th>
                      <td>
                        {{ item.start_date | formatDate }}
                        <strong class="mx-1">to</strong>
                        {{ item.end_date | formatDate }}
                      </td>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <th class="px-1">:</th>
                      <td>{{ item.description }}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <th class="px-1">:</th>
                      <td>{{ item.maintenance_status }}</td>
                    </tr>
                  </tbody>
                </table>
              </v-card-text>
            </v-card>
          </div>

          <div v-else class="text-center">
            <v-divider class="mb-4"></v-divider>
            <span>No Vehicle Maintenance to show</span>
          </div>
        </v-tab-item>

        <v-tab-item class="Height-Class">
          <DocumentList :documentList="vehicleDetails.vehicle_documents" />
        </v-tab-item>

        <v-tab-item>
          <v-sheet class="Height-Class">
            <v-timeline v-if="logs && logs.length" align-top dense>
              <v-timeline-item
                v-for="(log, index) in logs"
                :key="index"
                color="primary"
                icon="mdi-circle"
                small
              >
                <div>
                  <strong>{{ log.created | formatDate(true) }}</strong>
                  <div class="text-body-2">{{ log.message }}</div>
                  <div class="text-caption">
                    -
                    {{
                      log.updated_by
                        ? log.updated_by
                        : log.created_by
                        ? log.created_by
                        : ""
                    }}
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>

            <div v-else class="text-center">
              <v-divider class="mb-4"></v-divider>
              <span>No Logs to show</span>
            </div>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>

    <MaintenanceForm
      v-model="showMaintenanceForm"
      :asset="vehicleDetails"
      :editMode="editMode"
      :maintenanceId="selectedMaintenanceId"
      module="Vehicle"
      @closeMaintenanceForm="showMaintenanceForm = false"
      @refreshList="getVehicleMaintenanceList"
    />

    <AssetCheckListDetailsViewForm
      v-model="showAssetcheckListForm"
      :checkListItems="ChecklistDataItems"
    />

    <RenewConfirmationDialogue
      v-model="showAssetMaintenanceConfirmation"
      status="primary"
      title="Complete Maintenance"
      text="Are you sure you want to complete the asset maintenance?"
      @confirmed="completeMaintenanceStatus"
      @rejected="refreshCalls()"
    />
  </v-card>
</template>

<script>
import CardDetails from "@/components/General/CardDetails.vue";
import DocumentList from "@/components/General/DocumentList.vue";
import Tooltip from "@/components/General/Tooltip.vue";
import MaintenanceForm from "../dialogForms/MaintenanceForm.vue";
import AssetCheckListDetailsViewForm from "../dialogForms/AssetCheckListDetailsViewForm.vue";
import RenewConfirmationDialogue from "@/components/General/RenewConfirmationDialogue.vue";

export default {
  components: {
    CardDetails,
    Tooltip,
    MaintenanceForm,
    DocumentList,
    AssetCheckListDetailsViewForm,
    RenewConfirmationDialogue,
  },
  props: {
    vehicleId: {
      type: Number,
    },
    permissions: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    vehicleId(val) {
      this.logs = [];
      this.getVehicleDetails(val);
      this.vehicleActivityLogs();
      this.getVehicleMaintenanceList();
      this.getAssetChecklistData();
    },
  },
  data() {
    return {
      detailsTab: 0,
      vehicleDetails: {},
      vehicleMaintenance: [],
      showAssetMaintenanceConfirmation: false,
      logs: [],
      ChecklistDataItems: [],
      assetChecklistData: [],

      assetChecklistHeaders: [
        {
          text: "Date",
          align: "start",
          sortable: false,
          value: "date",
        },
        {
          text: "Is Submitted",
          value: "is_submitted",
          sortable: false,
          align: "center",
        },
        {
          text: "Is Passed",
          value: "is_passed",
          sortable: false,
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
        },
      ],

      showAssetcheckListForm: false,
      showMaintenanceForm: false,
      editMode: false,
      selectedMaintenanceId: null,

      vehicleDetailView: [0, 1],
    };
  },
  methods: {
    viewAssetChecklist(item, index) {
      this.ChecklistDataItems = item.items;
      this.showAssetcheckListForm = true;
    },
    getVehicleDetails(id) {
      this.$api.vehicle
        .getVehicleDetails(id)
        .then((res) => {
          this.vehicleDetails = res.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getVehicleMaintenanceList() {
      this.$api.maintenance
        .getMaintenanceList({ vehicle: this.vehicleId, limit: "all" })
        .then((res) => {
          this.vehicleMaintenance = res.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getAssetChecklistData(params = {}) {
      params = {
        limit: "all",
        vehicle: this.vehicleId,
        ...params,
      };
      this.$api.vehicle
        .getAssetChecklist(params)
        .then((res) => {
          this.assetChecklistData = res.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    addMaintenance() {
      this.editMode = false;
      this.selectedMaintenanceId = null;
      this.showMaintenanceForm = true;
    },
    editMaintenance(id) {
      this.editMode = true;
      this.selectedMaintenanceId = id;
      this.showMaintenanceForm = true;
    },
    vehicleActivityLogs(params = {}) {
      params = {
        ...params,
        vehicle: this.vehicleId,
        limit: "50",
        offset: this.logs.length,
      };
      this.$api.activityLogs
        .vehicleActivityLogs(params)
        .then((res) => {
          this.logs.push(...res.data);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    updateMAintenanceComplete(id) {
      this.selectedMaintenanceId = id;
      this.showAssetMaintenanceConfirmation = true;
    },
    completeMaintenanceStatus() {
      this.$bus.$emit("showLoader", true);
      this.$api.maintenance
        .completeMaintenance(this.selectedMaintenanceId)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          let message = res?.data?.message;
          if (message) {
            this.$bus.$emit("showToastMessage", {
              message: message,
              color: "success",
            });
          } else {
            this.$bus.$emit("showToastMessage", {
              message: "Vehicle status updated to 'Idle' successfully",
              color: "success",
            });
          }
          this.getVehicleMaintenanceList();
        })
        .catch((err) => {
          this.formErrors = err.data;
          if (err && err.data) {
            const showToast = (message) => {
              this.$bus.$emit("showToastMessage", {
                message,
                color: "error",
                hasActions: true,
              });
            };

            if (err.data.message) {
              showToast(err.data.message);
            } else if (
              err.data &&
              err.data.non_field_errors &&
              err.data.non_field_errors.length
            ) {
              showToast(err.data.non_field_errors.join(", "));
            } else {
              showToast("Vehicle Maintenance Update Failed");
            }
          }
          this.$bus.$emit("showLoader", false);
        });
    },
    getListData() {
      this.$bus.$emit("showLoader", true);
      Promise.all([
        this.getVehicleDetails(this.vehicleId),
        this.vehicleActivityLogs(),
        this.getVehicleMaintenanceList(),
        this.getAssetChecklistData(),
      ])
        .then((res) => {
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  mounted() {
    this.logs = [];
    this.getListData();
  },
};
</script>

<style scoped>
.Height-Class {
  height: calc(100vh - 282px);
  overflow-y: auto;
}

.Maintenance-Height-Class {
  height: calc(100vh - 322px);
  overflow-y: auto;
}
</style>
